import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, MoreVertical, Move, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { listPlatformNumbers, type PlatformNumber } from '@/api/endpoints/platform';
import { useOrganisations } from '@/hooks/queries/useAdmin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { errorCode } from '@/components/backoffice/platform/PlatformBits';
import { formatDate } from '@/i18n/locale';

// components/ui/select is typed without `id`; the trigger takes it (label association).
const SelectTriggerWithId = SelectTrigger as unknown as ComponentType<{ id: string; className?: string; children?: ReactNode }>;

/**
 * « Numéros » tab of TwilioConfig (W6, SR1-1.2): every organisation's numbers
 * in a DataTable paged by the server (GET /api/telephony/admin/all-numbers,
 * limit/offset/q, total), search on the server, hideable columns. Row
 * actions: reassign (dialog), release (ConfirmDialog, destructive). One CTA
 * when there is no number: « Synchroniser Twilio ».
 */
const PAGE_SIZE = 50;

function errMessage(err: unknown, fallback: string): string {
  const e = err as { error?: { message?: string }; message?: string } | undefined;
  return e?.error?.message ?? e?.message ?? fallback;
}

export function TwilioNumbersTab() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => { setQ(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const numbers = useQuery({
    queryKey: ['twilio-all-numbers', q, page],
    queryFn: ({ signal }) => listPlatformNumbers({ q, limit: PAGE_SIZE, offset: page * PAGE_SIZE }, signal),
    placeholderData: keepPreviousData,
  });
  const { data: orgsResp } = useOrganisations();
  const orgByEmail = useMemo(() => new Map((orgsResp?.organisations ?? []).map((o) => [o.email, o])), [orgsResp]);

  const [reassignTarget, setReassignTarget] = useState<PlatformNumber | null>(null);
  const [reassignTo, setReassignTo] = useState('');
  const [releaseTarget, setReleaseTarget] = useState<PlatformNumber | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['twilio-all-numbers'] });
  const unknownError = t('common:states.unknownError');

  const reassign = useMutation({
    mutationFn: async ({ id, tenantEmail }: { id: string; tenantEmail: string }) => (await api.functions.invoke('reassignTwilioNumber', { id, tenantEmail })).data,
    onSuccess: () => {
      void invalidate();
      toast.success(t('admin:twilioConfig.toasts.reassigned'));
      setReassignTarget(null);
    },
    onError: (err) => toast.error(t('admin:twilioConfig.toasts.reassignError', { error: errMessage(err, unknownError) }), errorCode(err) ? { description: errorCode(err) } : undefined),
  });
  const release = useMutation({
    mutationFn: async (id: string) => (await api.functions.invoke('releaseTenantNumber', { id })).data,
    onSuccess: () => {
      void invalidate();
      toast.success(t('admin:twilioConfig.toasts.released'));
      setReleaseTarget(null);
    },
    onError: (err) => toast.error(t('admin:twilioConfig.toasts.releaseError', { error: errMessage(err, unknownError) }), errorCode(err) ? { description: errorCode(err) } : undefined),
  });
  const sync = useMutation({
    mutationFn: async () => (await api.functions.invoke('syncTwilioNumbers', {})).data as { imported?: unknown[]; already_present?: number; total_twilio?: number },
    onSuccess: (data) => {
      void invalidate();
      toast.success(t('admin:twilioConfig.toasts.synced', { imported: data?.imported?.length ?? 0, present: data?.already_present ?? 0, total: data?.total_twilio ?? 0 }));
    },
    onError: (err) => toast.error(t('admin:twilioConfig.toasts.syncError', { error: errMessage(err, unknownError) }), errorCode(err) ? { description: errorCode(err) } : undefined),
  });

  const orgLabel = (email: string) => {
    if (!email) return t('admin:twilioConfig.numbers.unassigned');
    const org = orgByEmail.get(email);
    return org?.company_name ? `${org.company_name} · ${email}` : email;
  };

  const columns: DataTableColumn<PlatformNumber>[] = [
    { id: 'number', header: t('admin:twilioConfig.numbers.columns.number'), cell: (n) => <span className="font-mono tabular-nums">{n.phone_number}</span> },
    { id: 'organisation', header: t('admin:twilioConfig.numbers.columns.organisation'), cell: (n) => <span className="block max-w-[18rem] truncate" title={n.tenant_email}>{orgLabel(n.tenant_email)}</span> },
    { id: 'status', header: t('admin:twilioConfig.numbers.columns.status'), cell: (n) => t(`admin:twilioConfig.numbers.statuses.${['active', 'suspended', 'released', 'provisioning'].includes(n.status) ? n.status : 'other'}`, { status: n.status }) },
    { id: 'areaCode', header: t('admin:twilioConfig.numbers.columns.areaCode'), hideable: true, cell: (n) => (n.area_code ? `+${n.area_code}` : '—') },
    { id: 'capabilities', header: t('admin:twilioConfig.numbers.columns.capabilities'), hideable: true, defaultHidden: true, cell: (n) => n.capabilities.join(', ') || '—' },
    { id: 'provisionedAt', header: t('admin:twilioConfig.numbers.columns.provisionedAt'), hideable: true, defaultHidden: true, cell: (n) => (n.provisioned_at ? formatDate(n.provisioned_at) : '—') },
    {
      id: 'actions',
      header: <span className="sr-only">{t('admin:twilioConfig.numbers.actionsAriaLabel')}</span>,
      align: 'right',
      cell: (n) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded hover:bg-[var(--vf-surface-3)]" aria-label={t('admin:twilioConfig.numbers.actionsFor', { number: n.phone_number })}>
              <MoreVertical className="h-4 w-4" aria-hidden />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => { setReassignTarget(n); setReassignTo(n.tenant_email); }} className="gap-2">
              <Move className="h-4 w-4" aria-hidden />
              {t('admin:twilioConfig.numbers.reassignTo')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setReleaseTarget(n)} className="gap-2 text-[var(--vf-fill-danger)]">
              <Trash2 className="h-4 w-4" aria-hidden />
              {t('admin:twilioConfig.numbers.releaseAction')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const syncButton = (
    <Button type="button" variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending} className="gap-2" title={t('admin:twilioConfig.numbers.syncTooltip')}>
      {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden /> : <RefreshCw className="h-4 w-4" aria-hidden />}
      {t('admin:twilioConfig.numbers.sync')}
    </Button>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('admin:twilioConfig.numbers.filterPlaceholder')}
          aria-label={t('admin:twilioConfig.numbers.filterPlaceholder')}
          className="max-w-sm"
        />
        {syncButton}
      </div>
      <DataTable
        caption={t('admin:twilioConfig.numbers.caption')}
        columns={columns}
        rows={numbers.data?.numbers ?? []}
        getRowId={(n) => n.id}
        status={numbers.isLoading ? 'loading' : numbers.isError ? 'error' : 'ready'}
        error={numbers.isError ? { message: t('admin:twilioConfig.numbers.loadError'), code: errorCode(numbers.error), onRetry: () => void numbers.refetch() } : undefined}
        filters={q ? [{ id: 'q', label: t('admin:twilioConfig.numbers.searchPill', { q }), onRemove: () => setSearch('') }] : []}
        onClearFilters={() => setSearch('')}
        empty={{ title: t('admin:twilioConfig.numbers.empty'), action: syncButton }}
        pagination={{ page, pageSize: PAGE_SIZE, total: numbers.data?.total ?? null, onPageChange: setPage }}
        storageKey="twilio-numbers"
      />

      <Dialog open={!!reassignTarget} onOpenChange={(open) => { if (!open) setReassignTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin:twilioConfig.reassignDialog.title')}</DialogTitle>
            <DialogDescription>{t('admin:twilioConfig.reassignDialog.description', { number: reassignTarget?.phone_number ?? '' })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">{t('admin:twilioConfig.reassignDialog.currentTenant')}</p>
              <p className="text-sm">{reassignTarget ? orgLabel(reassignTarget.tenant_email) : ''}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reassign-to">{t('admin:twilioConfig.reassignDialog.newTenant')}</Label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTriggerWithId id="reassign-to">
                  <SelectValue placeholder={t('admin:twilioConfig.provisionDialog.selectOrgPlaceholder')} />
                </SelectTriggerWithId>
                <SelectContent>
                  {(orgsResp?.organisations ?? []).map((o) => (
                    <SelectItem key={o.id} value={o.email}>{o.company_name ? `${o.company_name} · ${o.email}` : o.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignTarget(null)}>{t('common:actions.cancel')}</Button>
            <Button
              onClick={() => reassignTarget && reassign.mutate({ id: reassignTarget.id, tenantEmail: reassignTo })}
              disabled={!reassignTo || reassignTo === reassignTarget?.tenant_email || reassign.isPending}
              className="gap-2"
            >
              {reassign.isPending ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden /> : <Move className="h-4 w-4" aria-hidden />}
              {t('admin:twilioConfig.reassignDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!releaseTarget}
        onOpenChange={(open) => { if (!open) setReleaseTarget(null); }}
        title={t('admin:twilioConfig.releaseDialog.title')}
        description={t('admin:twilioConfig.releaseDialog.descriptionFull', { number: releaseTarget?.phone_number ?? '', organisation: releaseTarget ? orgLabel(releaseTarget.tenant_email) : '' })}
        confirmLabel={t('admin:twilioConfig.releaseDialog.confirm')}
        busy={release.isPending}
        onConfirm={() => (releaseTarget ? release.mutateAsync(releaseTarget.id).then(() => undefined) : undefined)}
      />
    </div>
  );
}
